import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectUser } from '@/redux/slices/authSlice'
import { Banner, formatDate } from '@/components/ui'
import type { Tone } from '@/components/ui'
import '@/components/billing/billing.css'

/** One line above every page when billing needs the school's attention. The billing page has its own. */
const BillingBanner: React.FC = () => {
  const user = useSelector(selectUser)
  const location = useLocation()
  const billing = user?.billing

  if (!billing || location.pathname.startsWith('/billing') || location.pathname.startsWith('/get-started')) {
    return null
  }

  let tone: Tone = 'warning'
  let title = ''
  let short = ''
  let link = { to: '/billing', label: 'Open billing' }

  const daysLeft = (date: string | null | undefined) => (date ? Math.ceil((new Date(date).getTime() - Date.now()) / 86400000) : null)

  if (billing.accessMode === 'setup_required') {
    tone = 'info'
    title = 'Choose your plan to start your free trial. Every module is open during it.'
    short = 'Choose a plan to start your trial.'
    link = { to: '/get-started', label: 'Choose plan' }
  } else if (billing.accessMode === 'read_only') {
    tone = 'danger'
    title = 'Your school can only read data until the subscription is paid.'
    short = 'Read-only until payment.'
  } else if (billing.accessMode === 'core_only') {
    title = 'Only core pages are open. Choose modules to unlock the rest.'
    short = 'Only core pages are open.'
  } else if (billing.state === 'past_due_grace') {
    title = `Payment is overdue. Access becomes read-only on ${formatDate(billing.graceEndsAt)}.`
    short = `Payment overdue · read-only from ${formatDate(billing.graceEndsAt)}.`
  } else if (billing.state === 'trialing') {
    // The whole trial: say plainly that everything is open, and for how long.
    const left = daysLeft(billing.trialEndsAt)
    if (left == null) return null
    tone = 'info'
    const when = left <= 1 ? 'Last day' : `${left} days left`
    title = billing.plannedModules?.length
      ? `Free trial · every module open · ${when}. Your plan keeps ${billing.plannedModules.length} module${billing.plannedModules.length === 1 ? '' : 's'} after ${formatDate(billing.trialEndsAt)}.`
      : `Free trial · every module open · ${when}. Choose the modules you want to keep.`
    short = `Free trial · ${when}`
    link = { to: '/billing', label: billing.plannedModules?.length ? 'Manage plan' : 'Choose modules' }
  } else if (billing.state === 'grandfathered') {
    // A long free period (the launch offer) only needs a word in its last month.
    const left = daysLeft(billing.freeUntil)
    if (left != null && left > 30) return null
    tone = 'info'
    const lastFreeDay = billing.freeUntil ? formatDate(new Date(new Date(billing.freeUntil).getTime() - 1)) : null
    title = lastFreeDay
      ? `Free until ${lastFreeDay}. Choose the modules you want to keep so your school carries on without a break.`
      : 'Choose your modules before your free access ends.'
    short = lastFreeDay ? `Free until ${lastFreeDay}` : 'Choose modules before access ends.'
    link = { to: '/billing', label: 'Choose modules' }
  }

  if (!title) {
    return null
  }

  return (
    <div className="cb-app bl-shell-banner">
      <Banner
        tone={tone}
        title={
          <>
            <span className="bl-shell-banner__full">{title}</span>
            <span className="bl-shell-banner__short">{short || title}</span>
          </>
        }
      >
        <Link to={link.to} className="cb-link">{link.label}</Link>
      </Banner>
    </div>
  )
}

export default BillingBanner
